import { isEqual as defaultIsEqual } from "./is-equal";

type Func<T extends any[], R> = (...args: T) => R;

/**
 * Takes a function and returns a new memoized function.
 * If the same arguments are passed to the memoized function, it will return the previously computed result.
 * This is a "memoize once" function meaning it only compares the previous and current arguments, not historic values.
 * Results that throw errors or return rejected Promises are not cached.
 *
 * @param {Function} fn - The function to be memoized.
 * @param {(value: any) => boolean} [isEqual]- The equality function used to compare arguments.
 * @returns {Function} - The memoized function.
 * @param func
 */
export function memoizeLast<T extends any[], R>(func: Func<T, R>, { isEqual = defaultIsEqual } = {}): Func<T, R> {
  let lastArgs: T | null = null;
  let lastResult: R | null = null;
  let hasResult = false; // Separate variable to track whether lastResult exists because lastResult could be falsy
  let promiseCounter = 0; // Used to keep track of result order in async invocation so we know to replace cache or not

  return (...args: T): R => {
    if (hasResult && isEqual(lastArgs, args)) {
      return lastResult as R;
    }

    const result = func(...args); // Errors here are not caught, so result will not cache if there is an error

    if (result instanceof Promise) {
      promiseCounter++;
      const promiseCounterAtStart = promiseCounter;
      result.catch(() => {
        // Don't return cached result next time if the Promise rejects, unless a new Promise has already replaced
        // the one that rejected
        if (promiseCounterAtStart === promiseCounter) {
          hasResult = false;
        }
      });
    }

    // Cache the latest args and result.
    lastArgs = args;
    lastResult = result;
    hasResult = true;

    return result;
  };
}
